/**
 * TutorialCoach — 非模态教练标记引擎（SCREENS.md §12）。
 *
 * 数据驱动：步骤为纯数据数组，新增步骤不触碰引擎。
 * 与游戏逻辑零耦合 —— 教练只消费 GameScene 上报的 UI 事件（tutorialBus）
 * 与各场景注册的锚点解析器（返回屏幕 DOMRect，3D 锚点由场景投影）。
 *
 * 形态：压暗遮罩 + 锚点聚光切口（box-shadow 反选法）+ 气泡卡片
 * （标题/正文 + [跳过] [下一步]）。advanceOn 为事件名时随用户操作推进，
 * 为 'next' 时仅按钮推进。完成或跳过均置 tutorialDone 标记。
 */
import { $settings } from '../store/settingsStore';
import { tr, trf, type DictKey } from '../ui/i18n';

const DONE_KEY = 'mc_tutorial_done';

/** GameScene 上报的 UI 事件（步骤推进信号）。 */
export type TutorialEvent = 'marble-selected' | 'destination-tapped' | 'turn-ended';

export interface CoachStep {
  id: string;
  /** 锚点：返回屏幕矩形；null → 无聚光（全屏压暗 + 居中气泡）。 */
  anchor: () => DOMRect | null;
  titleKey: DictKey;
  bodyKey: DictKey;
  /** 'next' = 按钮推进；否则等待同名 tutorialBus 事件。 */
  advanceOn: 'next' | TutorialEvent;
}

export function isTutorialDone(): boolean {
  try {
    return localStorage.getItem(DONE_KEY) === '1';
  } catch {
    return true; // 存储不可用时不骚扰用户
  }
}

export function markTutorialDone(): void {
  try {
    localStorage.setItem(DONE_KEY, '1');
  } catch {
    /* 隐私模式下静默 */
  }
}

export function resetTutorial(): void {
  try {
    localStorage.removeItem(DONE_KEY);
  } catch {
    /* 同上 */
  }
}

// ── 事件总线：GameScene emit，Coach 订阅（跳过即退订，零残留） ──
type Listener = (e: TutorialEvent) => void;
const listeners = new Set<Listener>();

export function tutorialEmit(e: TutorialEvent): void {
  for (const fn of listeners) fn(e);
}

function tutorialOn(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ── 引擎 ──────────────────────────────────────────────────
export class TutorialCoach {
  private root: HTMLElement | null = null;
  private spotEl: HTMLElement | null = null;
  private bubbleEl: HTMLElement | null = null;
  private idx = 0;
  private offBus: (() => void) | null = null;
  private raf = 0;
  private onResize = (): void => this.layout();

  /**
   * @param dim 是否压暗全屏（联机轻量提示传 false —— 不遮 waiting room）。
   */
  constructor(
    private readonly host: HTMLElement,
    private readonly steps: CoachStep[],
    private readonly dim = true
  ) {}

  start(): void {
    if (this.steps.length === 0) return;
    const root = document.createElement('div');
    root.className = `coach-root${this.dim ? '' : ' coach-root--nodim'}`;
    this.root = root;

    const spot = document.createElement('div');
    spot.className = 'coach-spot';
    this.spotEl = spot;

    const bubble = document.createElement('div');
    bubble.className = 'coach-bubble';
    this.bubbleEl = bubble;

    root.append(spot, bubble);
    this.host.appendChild(root);
    window.addEventListener('resize', this.onResize);
    this.offBus = tutorialOn((e) => {
      const step = this.steps[this.idx];
      if (step && step.advanceOn === e) this.next();
    });
    this.renderStep();
    this.track();
  }

  stop(): void {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.onResize);
    this.offBus?.();
    this.offBus = null;
    this.root?.remove();
    this.root = null;
    this.spotEl = null;
    this.bubbleEl = null;
  }

  private finish(): void {
    markTutorialDone();
    this.stop();
  }

  private next(): void {
    this.idx++;
    if (this.idx >= this.steps.length) {
      this.finish();
      return;
    }
    this.renderStep();
  }

  private renderStep(): void {
    const step = this.steps[this.idx];
    const bubble = this.bubbleEl;
    if (!step || !bubble) return;
    const lang = $settings.get().language;

    bubble.innerHTML = '';
    const meta = document.createElement('div');
    meta.className = 'coach-meta';
    meta.textContent = trf('tutStep', lang, { a: this.idx + 1, b: this.steps.length });
    const title = document.createElement('div');
    title.className = 'coach-title';
    title.textContent = tr(step.titleKey, lang);
    const body = document.createElement('div');
    body.className = 'coach-body';
    body.textContent = tr(step.bodyKey, lang);

    const row = document.createElement('div');
    row.className = 'coach-actions';
    const skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'mini-btn';
    skip.textContent = tr('tutSkip', lang);
    skip.addEventListener('click', () => this.finish());
    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'btn btn-primary coach-next';
    const last = this.idx === this.steps.length - 1;
    next.textContent = last ? tr('tutFinish', lang) : tr('tutNext', lang);
    // 事件推进的步骤仍保留按钮作为兜底出口（可达性）。
    next.addEventListener('click', () => this.next());
    row.append(skip, next);
    bubble.append(meta, title, body, row);
    this.layout();
  }

  /** 聚光跟踪：rAF 逐帧解析锚点（相机可缓慢环绕，投影会漂）。 */
  private track = (): void => {
    this.raf = requestAnimationFrame(this.track);
    this.layout();
  };

  private layout(): void {
    const step = this.steps[this.idx];
    const spot = this.spotEl;
    const bubble = this.bubbleEl;
    if (!step || !spot || !bubble) return;
    const rect = step.anchor();
    const pad = 10;
    if (rect) {
      spot.style.display = '';
      spot.style.left = `${rect.left - pad}px`;
      spot.style.top = `${rect.top - pad}px`;
      spot.style.width = `${rect.width + pad * 2}px`;
      spot.style.height = `${rect.height + pad * 2}px`;
    } else {
      spot.style.display = 'none';
    }
    // 气泡定位：锚点下方优先，越界则上方；无锚点居中。
    const bw = Math.min(320, window.innerWidth - 32);
    bubble.style.width = `${bw}px`;
    const bh = bubble.offsetHeight || 140;
    let left: number;
    let top: number;
    if (rect) {
      left = Math.max(
        16,
        Math.min(rect.left + rect.width / 2 - bw / 2, window.innerWidth - bw - 16)
      );
      top = rect.bottom + pad + 12;
      if (top + bh > window.innerHeight - 16) top = Math.max(16, rect.top - pad - 12 - bh);
    } else {
      left = (window.innerWidth - bw) / 2;
      top = (window.innerHeight - bh) / 2;
    }
    bubble.style.left = `${left}px`;
    bubble.style.top = `${top}px`;
  }
}
